import { useEffect, useState } from 'react';
import api from '../../api/axios';
import StatusBadge from '../../components/StatusBadge';
import RequestFormModal from '../../components/RequestFormModal';
import RequestComments from '../../components/RequestComments';
import { MATERIAL_TABS } from './materialFieldConfig';
import { VENDOR_TABS } from './vendorFieldConfig';
import { toast } from 'react-toastify';

const TABS_BY_SOURCE = {
  material_requests: MATERIAL_TABS,
  vendor_requests: VENDOR_TABS,
};

const CODE_COLUMNS = {
  material_requests: 'material_number',
  vendor_requests: 'business_partner_number',
};

const fmt = v => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const RequestStatus = () => {
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modalReq, setModalReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Edit state (for rejected requests)
  const [editDraft, setEditDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);

  const load = () => api.get('/requests/my').then(r => setRequests(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openRow = r => {
    setSelected(r);
    setActiveTab(0);
    setEditDraft({});
  };

  const tabs = selected ? TABS_BY_SOURCE[selected.source] : null;
  const codeColumn = selected ? CODE_COLUMNS[selected.source] : null;
  const isRejected = selected?.status === 'rejected';
  const isNeedsInfo = selected?.status === 'needs_info';
  const canEdit = isRejected || isNeedsInfo;
  const activeFields = tabs?.[activeTab]?.fields || [];

  const valueOf = key => (key in editDraft ? editDraft[key] : selected?.[key]) ?? '';
  const changes = tabs
    ? tabs.flatMap(t => t.fields).reduce((acc, f) => {
        if (f.key === codeColumn || !(f.key in editDraft)) return acc;
        const orig = selected?.[f.key] === null || selected?.[f.key] === undefined ? '' : String(selected[f.key]);
        const cur = editDraft[f.key] === null || editDraft[f.key] === undefined ? '' : String(editDraft[f.key] ?? '');
        if (orig !== cur) acc[f.key] = editDraft[f.key] === '' ? null : editDraft[f.key];
        return acc;
      }, {})
    : {};
  const changeCount = Object.keys(changes).length;

  const handleSave = async () => {
    if (!changeCount) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/requests/${selected.id}/user-edit`, { source: selected.source, changes });
      toast.success(`${changeCount} field(s) saved`);
      // Preserve source/approvals/comments — /user-edit returns a raw DB row without them
      const updated = { ...selected, ...data, source: selected.source, approvals: selected.approvals, comments: selected.comments };
      setSelected(updated);
      setRequests(prev => prev.map(r => r.id === selected.id && r.source === selected.source ? { ...r, ...data } : r));
      setEditDraft({});
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleResubmit = async () => {
    setResubmitting(true);
    try {
      await api.patch(`/requests/${selected.id}/resubmit`, { source: selected.source });
      toast.success('Request resubmitted — awaiting approval');
      setSelected(null);
      setEditDraft({});
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Resubmit failed'); }
    finally { setResubmitting(false); }
  };

  return (
    <div style={S.layout}>
      {/* ── Table ── */}
      <div style={S.card}>
        <div style={S.cardTop}>
          <div style={S.cardTitle}>My Requests</div>
          <div style={S.badge}>{requests.length} total</div>
        </div>
        {loading ? <div style={S.loading}>Loading…</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr style={S.thead}>
                  {['Request #', 'Title', 'Category', 'Status', 'Comments', 'Approver Remarks', 'IT Remarks', 'Submitted'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 && (
                  <tr><td colSpan={8} style={S.empty}>No requests found. Submit your first request from the sidebar.</td></tr>
                )}
                {requests.map((r, i) => {
                  const count = r.comments?.length || 0;
                  const active = selected?.source === r.source && selected?.id === r.id;
                  return (
                    <tr key={`${r.source}-${r.id}`} onClick={() => openRow(r)}
                      style={{ ...S.tr, cursor: 'pointer', background: active ? '#f8faff' : i % 2 === 0 ? '#fff' : '#f8faff' }}>
                      <td style={{ ...S.td, fontWeight: 700, color: '#4f6ef7' }}>
                        <span onClick={e => { e.stopPropagation(); setModalReq(r); }} style={S.reqLink}>{r.request_number}</span>
                      </td>
                      <td style={{ ...S.td, fontWeight: 600, color: '#0B1554' }}>{r.title}</td>
                      <td style={S.td}><span style={S.catBadge}>{r.category}</span></td>
                      <td style={S.td}><StatusBadge status={r.status} /></td>
                      <td style={S.td}>{count > 0 ? <span style={S.commentPill}>{count}</span> : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td style={{ ...S.td, color: r.approver_remarks ? '#0B1554' : '#94a3b8', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.approver_remarks || '—'}</td>
                      <td style={{ ...S.td, color: r.it_remarks ? '#0B1554' : '#94a3b8', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.it_remarks || '—'}</td>
                      <td style={{ ...S.td, color: '#94a3b8' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Side Panel ── */}
      {selected && (
        <div style={S.panel}>
          <div style={S.panelHead}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.panelReqNum}>{selected.request_number}</div>
              <div style={S.panelMeta}>{selected.category}</div>
            </div>
            <button onClick={() => setSelected(null)} style={S.closeBtn}>✕</button>
          </div>

          <div style={S.panelScroll}>
            {/* Summary strip */}
            <div style={S.strip}>
              <div style={S.stripItem}>
                <span style={S.stripLabel}>Status</span>
                <StatusBadge status={selected.status} />
              </div>
              <div style={S.stripItem}>
                <span style={S.stripLabel}>Date</span>
                <span style={S.stripVal}>{new Date(selected.created_at).toLocaleDateString()}</span>
              </div>
              <div style={S.stripItem}>
                <span style={S.stripLabel}>Updated</span>
                <span style={S.stripVal}>{new Date(selected.updated_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Rejection banner */}
            {isRejected && (
              <div style={S.rejectedBanner}>
                <div style={S.rejectedTitle}>✕ Request Rejected</div>
                <div style={S.rejectedHint}>
                  Edit the fields below to address the feedback, then resubmit. The approval chain will restart from the beginning.
                </div>
                {selected.approver_remarks && (
                  <div style={S.rejectedRemarks}>
                    <span style={S.rejectedRemarksLabel}>Approver said:</span> {selected.approver_remarks}
                  </div>
                )}
              </div>
            )}

            {/* Needs info banner */}
            {isNeedsInfo && (
              <div style={S.needsInfoBanner}>
                <div style={S.needsInfoTitle}>⚠ IT Needs More Information</div>
                <div style={S.rejectedHint}>
                  IT has reviewed your request and needs additional details. Update the fields below and resubmit — it will go back through approval before reaching IT.
                </div>
              </div>
            )}

            {/* Discussion */}
            <div style={S.section}>
              <RequestComments
                request={selected}
                onPosted={comment => {
                  const updated = { ...selected, comments: [...(selected.comments || []), comment] };
                  setSelected(updated);
                  setRequests(prev => prev.map(r => r.id === updated.id && r.source === updated.source ? { ...r, comments: updated.comments } : r));
                }}
              />
            </div>

            {/* IT status */}
            {['approved', 'in_progress', 'completed'].includes(selected.status) && (
              <div style={S.section}>
                <div style={S.sectionLabel}>IT Processing</div>
                <div style={{ ...S.itBox, borderColor: selected.status === 'completed' ? '#bbf7d0' : '#fed7aa', background: selected.status === 'completed' ? '#f0fdf4' : '#fff7ed' }}>
                  <div style={S.itRow}>
                     <span style={{ ...S.itDot, background: selected.status === 'completed' ? '#4f6ef7' : selected.status === 'in_progress' ? '#f59e0b' : '#94a3b8' }} />

                    <span style={S.itStatusText}>{selected.status === 'completed' ? 'Completed' : selected.status === 'in_progress' ? 'In Progress' : 'Awaiting IT'}</span>
                  </div>
                  {selected.it_remarks && <div style={S.itRemarks}>{selected.it_remarks}</div>}
                  {selected.material_number && <div style={S.itCode}>Material #: <strong>{selected.material_number}</strong></div>}
                  {selected.business_partner_number && <div style={S.itCode}>BP #: <strong>{selected.business_partner_number}</strong></div>}
                </div>
              </div>
            )}

            {/* Field data — editable if rejected, read-only otherwise */}
            {tabs && (
              <div style={S.section}>
                <div style={S.sectionRow}>
                  <div style={S.sectionLabel}>{selected.category} Data</div>
                  {changeCount > 0 && <span style={S.changePill}>{changeCount} unsaved</span>}
                </div>

                <div style={S.tabBar}>
                  {tabs.map((t, i) => {
                    const filled = t.fields.filter(f => fmt(selected[f.key]) !== null).length;
                    const touched = t.fields.filter(f => f.key in editDraft).length;
                    return (
                      <button key={t.id} type="button" onClick={() => setActiveTab(i)}
                        style={{ ...S.tab, ...(i === activeTab ? { ...S.tabActive, boxShadow: 'inset 0 -3px 0 #4f6ef7' } : null) }}>
                        {t.label}
                        {filled > 0 && <span style={S.tabCount}>{filled}</span>}
                        {touched > 0 && <span style={{ ...S.tabCount, background: '#fef3c7', color: '#b45309' }}>{touched}*</span>}
                      </button>
                    );
                  })}
                </div>

                <div style={S.fieldGrid}>
                  {activeFields.map(f => {
                    const locked = f.key === codeColumn;
                    const val = fmt(selected[f.key]);
                    const changed = f.key in editDraft && String(editDraft[f.key] ?? '') !== String(selected[f.key] ?? '');
                    return (
                      <div key={f.key} style={S.field}>
                        <label style={S.fieldLabel}>
                          {f.label}
                          {f.required && <span style={S.req}> *</span>}
                          {locked && <span style={S.lockTag}>IT only</span>}
                          {changed && <span style={S.changedTag}>edited</span>}
                        </label>
                        {canEdit && !locked ? (
                          <input
                            style={{ ...S.fieldInput, ...(changed ? S.fieldInputChanged : null) }}
                            type={f.type || 'text'}
                            value={valueOf(f.key)}
                            onChange={e => setEditDraft(prev => ({ ...prev, [f.key]: e.target.value }))}
                            placeholder={`Enter ${f.label}`}
                          />
                        ) : (
                          <div style={{ ...S.fieldValue, color: val ? '#0B1554' : '#94a3b8' }}>
                            {locked && !val ? 'Filled in by IT at completion' : val || '—'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Save + Resubmit actions */}
                {canEdit && (
                  <div style={S.actionBox}>
                    {changeCount > 0 && (
                      <button style={{ ...S.btnSave, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : `Save ${changeCount} change${changeCount === 1 ? '' : 's'}`}
                      </button>
                    )}
                    <button
                      style={{ ...S.btnResubmit, opacity: resubmitting || changeCount > 0 ? (changeCount > 0 ? 0.4 : 0.6) : 1 }}
                      onClick={handleResubmit}
                      disabled={resubmitting || changeCount > 0}
                      title={changeCount > 0 ? 'Save your changes first before resubmitting' : ''}>
                      {resubmitting ? 'Resubmitting…' : isNeedsInfo ? '↺ Resubmit to IT' : '↺ Resubmit for Approval'}
                    </button>
                    {changeCount > 0 && <div style={S.saveFirst}>Save your changes before resubmitting</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {modalReq && <RequestFormModal request={modalReq} onClose={() => setModalReq(null)} />}
    </div>
  );
};

const S = {
  layout: { display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' },
  card: { flex: 2, minWidth: 0, background: '#fff', borderRadius: 18, boxShadow: '0 4px 20px rgba(11,21,84,0.06)', overflow: 'hidden', border: '1px solid rgba(79,110,247,0.06)' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #f1f5f9' },
  cardTitle: { fontSize: 14, fontWeight: 800, color: '#0B1554' },
  badge: { background: '#f0f4ff', color: '#4f6ef7', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 },
  loading: { padding: 40, textAlign: 'center', color: '#94a3b8' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'linear-gradient(135deg,#060d3a,#0d1a6e)' },
  th: { padding: '12px 18px', color: '#F7D506', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '13px 18px', fontSize: 13.5, color: '#374151' },
  empty: { textAlign: 'center', padding: '40px 20px', color: '#94a3b8', fontSize: 14 },
  catBadge: { background: '#f0f4ff', color: '#4f6ef7', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6 },
  reqLink: { cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', color: '#4f6ef7' },
  commentPill: { background: '#eef1f7', color: '#4f6ef7', fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 20 },

  panel: { width: 460, flexShrink: 0, background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(11,21,84,0.12)', overflow: 'hidden', border: '1px solid rgba(79,110,247,0.08)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' },
  panelHead: { background: 'linear-gradient(135deg,#060d3a,#0d1a6e)', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 },
  panelReqNum: { color: '#F7D506', fontWeight: 800, fontSize: 14, marginBottom: 2 },
  panelMeta: { color: 'rgba(255,255,255,0.6)', fontSize: 12 },
  closeBtn: { background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },
  panelScroll: { overflowY: 'auto', flex: 1 },

  strip: { display: 'flex', borderBottom: '1px solid #f1f5f9' },
  stripItem: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 16px', borderRight: '1px solid #f1f5f9' },
  stripLabel: { fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.7, textTransform: 'uppercase' },
  stripVal: { fontSize: 13, fontWeight: 700, color: '#0B1554' },

  rejectedBanner: { margin: '14px 20px 0', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '12px 14px' },
  rejectedTitle: { fontSize: 13, fontWeight: 800, color: '#dc2626', marginBottom: 4 },
  needsInfoBanner: { margin: '14px 20px 0', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 12, padding: '12px 14px' },
  needsInfoTitle: { fontSize: 13, fontWeight: 800, color: '#0e7490', marginBottom: 4 },
  rejectedHint: { fontSize: 12, color: '#7f1d1d', lineHeight: 1.5, marginBottom: 6 },
  rejectedRemarks: { fontSize: 12.5, color: '#374151', background: '#fff', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', lineHeight: 1.5 },
  rejectedRemarksLabel: { fontWeight: 700, color: '#dc2626' },

  section: { padding: '14px 20px', borderBottom: '1px solid #f1f5f9' },
  sectionRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  sectionLabel: { fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase' },
  changePill: { fontSize: 10.5, fontWeight: 800, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: 20 },

  itBox: { border: '1px solid', borderRadius: 10, padding: '10px 14px' },
  itRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  itDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  itStatusText: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  itRemarks: { fontSize: 12.5, color: '#374151', lineHeight: 1.5, marginBottom: 4 },
  itCode: { fontSize: 12.5, color: '#374151', marginTop: 4 },

  tabBar: { display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 12, borderBottom: '1px solid #eef1f7' },
  tab: { background: 'none', border: 'none', cursor: 'pointer', padding: '7px 10px', fontSize: 11.5, fontWeight: 700, color: '#8892b0', whiteSpace: 'nowrap', borderRadius: '6px 6px 0 0' },
  tabActive: { color: '#0B1554', background: '#f8faff' },
  tabCount: { display: 'inline-block', minWidth: 14, padding: '0 4px', marginLeft: 4, borderRadius: 7, background: '#eef1f7', color: '#8892b0', fontSize: 9.5, fontWeight: 700, textAlign: 'center' },

  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' },
  field: { marginBottom: 12 },
  fieldLabel: { display: 'block', fontSize: 11.5, fontWeight: 600, color: '#4a5568', marginBottom: 4 },
  req: { color: '#DD3222' },
  lockTag: { marginLeft: 5, fontSize: 9, fontWeight: 800, color: '#64748b', background: '#eef1f7', padding: '1px 5px', borderRadius: 4 },
  changedTag: { marginLeft: 5, fontSize: 9, fontWeight: 800, color: '#b45309', background: '#fef3c7', padding: '1px 5px', borderRadius: 4 },
  fieldInput: { width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#0B1554', background: '#f8faff', boxSizing: 'border-box' },
  fieldInputChanged: { borderColor: '#f59e0b', background: '#fffbeb', fontWeight: 700 },
  fieldValue: { padding: '8px 10px', border: '1.5px solid #f0f2f8', borderRadius: 8, fontSize: 13, background: '#f8faff', minHeight: 36 },

  actionBox: { marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  btnSave: { padding: '10px', background: 'linear-gradient(135deg,#0d1a6e,#4f6ef7)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
   btnResubmit: { padding: '11px', background: 'linear-gradient(135deg,#1A2C80,#4f6ef7)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13.5, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 14px rgba(26,44,128,0.25)' },

  saveFirst: { fontSize: 11, color: '#b45309', textAlign: 'center' },
};

export default RequestStatus;
